import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  SiApplicationHeaderComponent,
  SiHeaderAccountItemComponent,
  SiHeaderActionItemComponent,
  SiHeaderActionsDirective,
  SiHeaderBrandDirective,
  SiHeaderLogoDirective,
  SiHeaderNavigationComponent,
  SiHeaderNavigationItemComponent
} from '@siemens/element-ng/application-header';
import { SiFormModule } from '@siemens/element-ng/form';
import {
  SiHeaderDropdownComponent,
  SiHeaderDropdownItemComponent,
  SiHeaderDropdownTriggerDirective
} from '@siemens/element-ng/header-dropdown';
import { SiLoadingSpinnerDirective } from '@siemens/element-ng/loading-spinner';
import { MenuItemBase, SiMenuModule } from '@siemens/element-ng/menu';
import { SiNavbarVerticalModule } from '@siemens/element-ng/navbar-vertical';
import { SiPillsInputModule } from '@siemens/element-ng/pills-input';
import { SiSearchBarModule } from '@siemens/element-ng/search-bar';
import { SiSelectModule } from '@siemens/element-ng/select';
import { SiSidePanelModule } from '@siemens/element-ng/side-panel';
import { ThemeType } from '@siemens/element-ng/theme';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';

import { Schedule } from './shared/DataTypes';
import { AccountService, UserRole } from './shared/services/account.service';
import { AuthBootstrapService } from './shared/services/auth-bootstrap.service';
import { EventService } from './shared/services/event.service';
import { SettingsService } from './shared/services/settings.service';
import { ShareDataService } from './shared/services/share-data.service';
import { TourService } from './shared/services/tour.service';
import { WorkspaceService } from './shared/services/workspace.service';
import { WorkspacesComponent } from './workspaces/workspaces.component';

const REPORT_PROBLEM_FORM_URL = 'https://forms.cloud.microsoft/r/vZuFNZ84Ch?embed=true';

@Component({
  selector: 'app-main',
  imports: [
    RouterLinkActive,
    SiApplicationHeaderComponent,
    SiHeaderDropdownComponent,
    SiHeaderDropdownItemComponent,
    SiHeaderDropdownTriggerDirective,
    SiHeaderLogoDirective,
    SiHeaderNavigationItemComponent,
    SiHeaderNavigationComponent,
    SiHeaderBrandDirective,
    SiHeaderActionItemComponent,
    SiHeaderActionsDirective,
    RouterLink,
    TranslateModule,
    SiNavbarVerticalModule,
    SiMenuModule,
    SiSearchBarModule,
    RouterOutlet,
    FormsModule,
    SiFormModule,
    SiPillsInputModule,
    ReactiveFormsModule,
    SiSidePanelModule,
    SiLoadingSpinnerDirective,
    SiHeaderAccountItemComponent,
    SiSelectModule,
    WorkspacesComponent
  ],
  templateUrl: './main.component.html'
})
export class MainComponent implements OnInit {
  private eventService = inject(EventService);
  private workspaceService = inject(WorkspaceService);

  private settingsService = inject(SettingsService);
  private toastNotificationService = inject(SiToastNotificationService);

  private tourService = inject(TourService);
  private accountService = inject(AccountService);
  private readonly shareDataService = inject(ShareDataService);
  private readonly authBootstrapService = inject(AuthBootstrapService);

  loading = false;

  accountSummary = this.accountService.accountSummary;
  profilePhotoUrl = this.accountService.profilePhotoUrl;

  ngOnInit(): void {
    // Initialize application-specific services
    this.shareDataService.initializeRuntimeConfig().subscribe(runtimeConfig => {
      const userIsAdmin = this.accountService.hasRequiredRoles([UserRole.ADMIN]);
      if (runtimeConfig.redirectToProd && !userIsAdmin) {
        window.location.href = 'https://copilot.mobility.siemens.com/tools/smart-quick-sim';
      }
    });
    this.authBootstrapService.initialize();
    this.tourService.initialize();

    this.loading = true;

    // Subscribe to Event to receive simulating schedule
    this.eventService.simulatingScheduleChangedEvent$.subscribe(
      (simulatingSchedule: Schedule | null) => {
        this.simulatingSchedule.set(simulatingSchedule);
      }
    );

    this.workspaceService.getAllWorkspaces().subscribe({
      next: workspaces => {
        this.shareDataService.setAvailableWorkspaces(workspaces);

        // Stop loading after workspaces are loaded
        this.loading = false;
      }
    });

    this.fullApplicationTitle = this.shareDataService.getFullApplicationName();
  }

  fullApplicationTitle = '';
  readonly reportProblemUrl = this.sanitizeReportProblemUrl(REPORT_PROBLEM_FORM_URL);

  sidebarCollapsed = true;

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  readonly availableWorkspaces = this.shareDataService.availableWorkspaces;

  languageItems: MenuItemBase[] = this.settingsService.availableLanguages.map(language => ({
    id: language,
    label: `LANGUAGE.${language.toUpperCase()}`
  }));
  themeItems: MenuItemBase[] = [
    { id: 'auto', label: 'THEME.AUTO' },
    { id: 'light', label: 'THEME.LIGHT' },
    { id: 'dark', label: 'THEME.DARK' }
  ];

  readonly simulatingSchedule = signal<Schedule | null>(null);

  downloadUserManual() {
    this.workspaceService.downloadManual().subscribe({
      next: (response: Blob) => {
        // Create a blob URL and trigger download
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Manual_Schnellauslegungstool.pptx';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: error => {
        this.toastNotificationService.showToastNotification({
          state: 'danger',
          title: 'Error downloading Manual',
          message: error.statusText
        });
      }
    });
  }

  switchLanguage(language: string) {
    this.settingsService.setLanguage(language);
  }

  switchTheme(theme: string) {
    this.settingsService.setTheme(theme as ThemeType);
  }

  getTheme(): ThemeType {
    return this.settingsService.theme();
  }

  getLanguage(): string {
    return this.settingsService.language();
  }

  onCancelSimulation() {
    this.eventService.cancelSimulation();
  }

  /**
   * Starts the onboarding tour manually from the help menu
   * Force starts the tour even if it was previously completed
   */
  startTour(): void {
    this.tourService.startTour('onboarding', true);
  }

  private sanitizeReportProblemUrl(url: string): string {
    try {
      const parsedUrl = new URL(url);
      const isAllowedOrigin = parsedUrl.origin === 'https://forms.cloud.microsoft';
      const isAllowedProtocol = parsedUrl.protocol === 'https:';

      if (!isAllowedOrigin || !isAllowedProtocol) {
        return 'about:blank';
      }

      return parsedUrl.toString();
    } catch {
      return 'about:blank';
    }
  }
}
